<template>
  <div class="c-palette-configurator flex flex-col items-center gap-2">
    <COverflowWrapper ref="overflowWrapperRef" class="w-full" :max-height="220">
      <Draggable
        v-if="allowOrder"
        v-model="paletteItems"
        handle=".color-item-handle"
        item-key="id"
        @end="updatePalette"
      >
        <div v-for="(item, index) in paletteItems" :key="item.id" class="flex items-center gap-2 mb-2">
          <CIcon
            v-if="allowOrder"
            class="color-item-handle px-1 cursor-move flex-shrink-0 color-primary3"
            fa-type="fas"
            icon="grip-dots-vertical"
            size="16px"
          />
          <NColorPicker
            v-model:value="item.color"
            :actions="['clear', 'confirm']"
            default-value=""
            :disabled="!allowEdit"
            :modes="['hex']"
            :on-clear="() => onClear(index)"
            @update:value="updatePalette"
          />
          <CButton
            v-if="allowDelete"
            circle
            class="bg-error2"
            :disabled="paletteItems.length === 1"
            size="small"
            :style="{
              '--n-border': '1px solid transparent',
              '--n-border-disabled': '1px solid transparent',
              '--n-border-focus': '1px solid transparent',
              '--n-border-hover': '1px solid transparent',
              '--n-border-pressed': '1px solid transparent',
              '--n-color-focus': themeVars.error2,
              '--n-color-hover': themeVars.error2,
              '--n-color-disabled': themeVars.error2,
              '--n-color-pressed': themeVars.error2,
            }"
            type="error"
            @click="deleteRow(index)"
          >
            <template #icon>
              <CIcon class="color-error" fa-type="far" icon="trash" :size="12" />
            </template>
          </CButton>
        </div>
      </Draggable>
      <template v-else>
        <div v-for="(item, index) in paletteItems" :key="item.id" class="flex items-center gap-2 mb-2">
          <NColorPicker
            v-model:value="item.color"
            :actions="['clear', 'confirm']"
            default-value=""
            :disabled="!allowEdit"
            :modes="['hex']"
            :on-clear="() => onClear(index)"
            @update:value="updatePalette"
          />
          <CButton
            v-if="allowDelete"
            circle
            class="bg-error2"
            :disabled="paletteItems.length === 1"
            size="small"
            :style="{
              '--n-border': '1px solid transparent',
              '--n-border-disabled': '1px solid transparent',
              '--n-border-focus': '1px solid transparent',
              '--n-border-hover': '1px solid transparent',
              '--n-border-pressed': '1px solid transparent',
              '--n-color-focus': themeVars.error2,
              '--n-color-hover': themeVars.error2,
              '--n-color-disabled': themeVars.error2,
              '--n-color-pressed': themeVars.error2,
            }"
            type="error"
            @click="deleteRow(index)"
          >
            <template #icon>
              <CIcon class="color-error" fa-type="far" icon="trash" :size="12" />
            </template>
          </CButton>
        </div>
      </template>
    </COverflowWrapper>
    <CButton v-if="allowAdd" class="mt-2" icon-placement="left" size="small" @click="addRow">
      <template #icon>
        <CIcon fa-type="fas" icon="plus" />
      </template>
      {{ t('canvasUI.CPaletteConfigurator.addColor') }}
    </CButton>
  </div>
</template>

<script setup lang="ts">
import { NColorPicker } from 'naive-ui'
import { ref, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable as Draggable } from 'vue-draggable-plus'
import { v4 as uuid } from 'uuid'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import COverflowWrapper from '@lib/components/COverflowWrapper/COverflowWrapper.vue'

const { t } = useI18n()
import useThemeVars from '@lib/composables/useThemeVars'

const DEFAULT_COLOR = ''

const props = withDefaults(
  defineProps<{
    initialPalette: string[]
    allowOrder?: boolean
    allowAdd?: boolean
    allowDelete?: boolean
    allowEdit?: boolean
  }>(),
  {
    allowOrder: false,
    allowAdd: true,
    allowDelete: true,
    allowEdit: true,
  },
)

const emit = defineEmits<{ 'update:palette': [p: string[]] }>()

const themeVars = useThemeVars()
const overflowWrapperRef = ref<InstanceType<typeof COverflowWrapper> | null>(null)
const paletteItems = ref<Array<{ id: string; color: string }>>([])

function addRow() {
  paletteItems.value.push({ id: uuid(), color: DEFAULT_COLOR })
  updatePalette()
  nextTick(() => scrollToBottom())
}

function scrollToBottom() {
  const scrollContainer = overflowWrapperRef.value?.$el
  if (!scrollContainer) return
  scrollContainer.scrollTop = scrollContainer.scrollHeight
}

function deleteRow(index: number) {
  paletteItems.value.splice(index, 1)
  updatePalette()
}

function onClear(index: number) {
  paletteItems.value[index].color = DEFAULT_COLOR
  updatePalette()
}

function updatePalette() {
  const colors = paletteItems.value.map((item) => item.color)
  emit('update:palette', colors)
}

onMounted(() => {
  if (props.initialPalette.length) {
    paletteItems.value = props.initialPalette.map((color) => ({
      id: uuid(),
      color,
    }))
  } else {
    paletteItems.value = [{ id: uuid(), color: DEFAULT_COLOR }]
  }
})
</script>
